import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Calculator, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, QuoteLines, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { contact_name: "", email: "", phone: "", event_date: "", guest_count: 100, location_type: "private", budget: "", notes: "" };

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [venues, setVenues] = useState([]);
  const [providers, setProviders] = useState([]);
  const [menus, setMenus] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteVenue, setQuoteVenue] = useState("");
  const [unitOptions, setUnitOptions] = useState([]);
  const [selUnits, setSelUnits] = useState([]);
  const [selProviders, setSelProviders] = useState([]);
  const [menuId, setMenuId] = useState("");
  const [converting, setConverting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/enquiries", { params: showArchived ? { archived: true } : {} }).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, [showArchived]);

  useEffect(() => {
    api.get("/venues").then((r) => setVenues(r.data.filter((v) => !v.archived))).catch(() => {});
    api.get("/providers").then((r) => setProviders(r.data.filter((p) => !p.archived))).catch(() => {});
    api.get("/menus").then((r) => setMenus(r.data.filter((m) => !m.archived))).catch(() => {});
  }, []);

  const createEnquiry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/enquiries", { ...form, guest_count: Number(form.guest_count), budget: form.budget === "" ? null : Number(form.budget) });
      toast.success("Enquiry captured — CRM profile linked");
      setCreateOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openDetail = (enq) => {
    setSelected(enq);
    setQuote(enq.quote || null);
    setQuoteVenue(enq.venue_id || "");
    setSelUnits([]);
    setSelProviders([]);
    setMenuId("");
    api.get(`/bookings/feasibility/check?date=${enq.event_date}`).then((r) => setUnitOptions(r.data.units)).catch(() => setUnitOptions([]));
  };

  const calcQuote = (provIds = selProviders, venueId = quoteVenue) => {
    if (!selected) return;
    setQuoting(true);
    setQuote(null);
    setTimeout(async () => {
      try {
        const { data } = await api.get(`/enquiries/${selected.id}/quote`, {
          params: { venue_id: venueId || undefined, provider_ids: provIds.length ? provIds.join(",") : undefined, menu_id: menuId || undefined },
        });
        setQuote(data);
        setSelected((s) => ({ ...s, status: s.status === "new" ? "quoted" : s.status }));
        load();
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setQuoting(false);
      }
    }, 900);
  };

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const convert = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/enquiries/${selected.id}/convert`, {
        venue_id: quoteVenue || null,
        unit_ids: selUnits,
        provider_ids: selProviders,
        menu_id: menuId || null,
      });
      toast.success(`Booking ${data.booking_ref} created`);
      navigate(`/bookings/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setConverting(false);
    }
  };

  const markLost = async () => {
    try {
      await api.patch(`/enquiries/${selected.id}/status`, { status: "lost" });
      toast.success("Enquiry marked as lost");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const archiveEnquiry = async () => {
    try {
      await api.patch(`/enquiries/${selected.id}`, { archived: true });
      toast.success("Enquiry archived");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteEnquiry = async () => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await api.delete(`/enquiries/${selected.id}`);
      toast.success("Enquiry deleted");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="enquiries-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Enquiries</h1>
          <p className="mt-1 text-sm text-muted-foreground">Inbound leads from first contact to confirmed booking.</p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="toggle-archived-enquiries" onClick={() => setShowArchived(!showArchived)}
            className={`text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-150 ${showArchived ? "text-brand" : "text-muted-foreground hover:text-ink"}`}>
            {showArchived ? "← Back to active" : "View archived"}
          </button>
          <Button data-testid="new-enquiry-button" onClick={() => setCreateOpen(true)} className="rounded-md bg-ink hover:bg-ink-light">
            <Plus className="mr-2 h-4 w-4" /> New Enquiry
          </Button>
        </div>
      </div>

      <div className="border border-border bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              {["Contact", "Email", "Event Date", "Guests", "Type", "Status"].map((h) => (
                <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr
                key={e.id}
                data-testid={`enquiry-row-${e.id}`}
                onClick={() => openDetail(e)}
                className="cursor-pointer border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50"
              >
                <td className="px-5 py-3 font-medium">{e.contact_name}</td>
                <td className="px-5 py-3 text-muted-foreground">{e.email}</td>
                <td className="px-5 py-3 tabular-nums">{e.event_date}</td>
                <td className="px-5 py-3 tabular-nums">{e.guest_count}</td>
                <td className="px-5 py-3 capitalize">{e.location_type}</td>
                <td className="px-5 py-3"><StatusBadge status={e.status} /></td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No enquiries yet. Capture your first lead.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="new-enquiry-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Enquiry</DialogTitle></DialogHeader>
          <form onSubmit={createEnquiry} className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Contact name</Label><Input data-testid="enquiry-name-input" required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="enquiry-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="enquiry-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Event date</Label><Input data-testid="enquiry-date-input" type="date" min={new Date().toISOString().split("T")[0]} required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Guest count</Label><Input data-testid="enquiry-guests-input" type="number" min="1" required value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Budget (£, optional)</Label><Input data-testid="enquiry-budget-input" type="number" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Event type</Label>
              <Select value={form.location_type} onValueChange={(v) => setForm({ ...form, location_type: v })}>
                <SelectTrigger data-testid="enquiry-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                  <SelectItem value="public">Public / Trading</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea data-testid="enquiry-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="col-span-2">
              <Button data-testid="enquiry-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">Capture Enquiry</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl bg-white" data-testid="enquiry-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between pr-6">
                  <DialogTitle className="font-heading">{selected.contact_name}</DialogTitle>
                  <StatusBadge status={selected.status} />
                </div>
              </DialogHeader>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="border border-border p-4 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Event specifics</p>
                    <dl className="mt-3 space-y-2">
                      <div className="flex justify-between"><dt className="text-muted-foreground">Date</dt><dd className="font-medium tabular-nums">{selected.event_date}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Guests</dt><dd className="font-medium tabular-nums">{selected.guest_count}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Type</dt><dd className="font-medium capitalize">{selected.location_type}</dd></div>
                      {selected.venue_name && (
                        <div className="flex justify-between" data-testid="enquiry-custom-venue">
                          <dt className="text-muted-foreground">Venue (customer)</dt>
                          <dd className="font-medium text-right">{selected.venue_name}{selected.venue_address ? ` — ${selected.venue_address}` : ""}</dd>
                        </div>
                      )}
                      <div className="flex justify-between"><dt className="text-muted-foreground">Budget</dt><dd className="font-medium tabular-nums">{selected.budget ? fmt(selected.budget) : "—"}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd className="font-medium">{selected.email}</dd></div>
                    </dl>
                    {selected.notes && <p className="mt-3 border-t border-border pt-3 text-muted-foreground">{selected.notes}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label>Venue</Label>
                    <Select value={quoteVenue} onValueChange={(v) => { setQuoteVenue(v); setQuote(null); }}>
                      <SelectTrigger data-testid="quote-venue-select"><SelectValue placeholder="Select venue…" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {venues.map((v) => <SelectItem key={v.id} value={v.id} data-testid={`quote-venue-${v.id}`}>{v.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Menu package</Label>
                    <Select value={menuId} onValueChange={(v) => { setMenuId(v); setQuote(null); }}>
                      <SelectTrigger data-testid="quote-menu-select"><SelectValue placeholder="Standard tiered pricing" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {menus.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`quote-menu-${m.id}`}>{m.name} — {fmt(m.price_per_head)}/head</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button data-testid="calculate-quote-button" onClick={() => calcQuote()} disabled={quoting} variant="outline" className="w-full rounded-md border-ink text-ink hover:bg-muted">
                    <Calculator className="mr-2 h-4 w-4" />
                    {quoting ? "Calculating distance & feasibility…" : "Calculate Distance & Quote"}
                  </Button>
                  {quoting && <div data-testid="quote-loading" className="h-1 w-full animate-pulse bg-brand/40" />}
                  {quote && <QuoteLines quote={quote} />}
                </div>

                <div className="space-y-4">
                  <div className="border border-border p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Fleet feasibility — {selected.event_date}</p>
                    <div className="mt-3 space-y-2">
                      {unitOptions.map((u) => (
                        <label key={u.id} className={`flex items-center gap-3 border border-border px-3 py-2 text-sm transition-colors duration-150 ${u.available ? "cursor-pointer hover:bg-slate-50" : "cursor-not-allowed opacity-50"}`} data-testid={`unit-option-${u.id}`}>
                          <Checkbox
                            disabled={!u.available}
                            checked={selUnits.includes(u.id)}
                            onCheckedChange={() => toggle(selUnits, setSelUnits, u.id)}
                            data-testid={`unit-checkbox-${u.id}`}
                          />
                          <span className="flex-1 font-medium">{u.name}</span>
                          <StatusBadge status={u.available ? "active" : u.status === "active" ? "deployed" : u.status} />
                        </label>
                      ))}
                      {unitOptions.length === 0 && <p className="text-sm text-muted-foreground">No units in fleet.</p>}
                    </div>
                  </div>

                  <div className="border border-border p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">External providers</p>
                    <div className="mt-3 space-y-2">
                      {providers.map((p) => (
                        <label key={p.id} className="flex cursor-pointer items-center gap-3 border border-border px-3 py-2 text-sm transition-colors duration-150 hover:bg-slate-50" data-testid={`provider-option-${p.id}`}>
                          <Checkbox
                            checked={selProviders.includes(p.id)}
                            onCheckedChange={() => { const next = selProviders.includes(p.id) ? selProviders.filter((x) => x !== p.id) : [...selProviders, p.id]; setSelProviders(next); if (quote) calcQuote(next); }}
                            data-testid={`provider-checkbox-${p.id}`}
                          />
                          <span className="flex-1 font-medium">{p.name}</span>
                          <span className="text-xs text-muted-foreground capitalize">{p.category}</span>
                          <span className="text-xs font-semibold tabular-nums">{fmt(p.rate)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Button
                      data-testid="convert-booking-button"
                      onClick={convert}
                      disabled={converting || selected.status === "converted"}
                      className="flex-1 rounded-md bg-brand hover:bg-brand-dark"
                    >
                      {converting ? "Converting…" : "Convert to Booking"}
                    </Button>
                    {selected.status !== "converted" && selected.status !== "lost" && (
                      <Button data-testid="mark-lost-button" variant="outline" onClick={markLost} className="rounded-md border-red-300 text-red-600 hover:bg-red-50">
                        <X className="mr-1 h-4 w-4" /> Lost
                      </Button>
                    )}
                    <Button data-testid="archive-enquiry-button" variant="outline" onClick={archiveEnquiry} className="rounded-md">
                      <Archive className="mr-1 h-4 w-4" /> Archive
                    </Button>
                    <Button data-testid="delete-enquiry-button" variant="outline" onClick={deleteEnquiry} className="rounded-md border-red-300 text-red-600 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
